// bluaIQ — Error Boundary
// Catches React rendering errors, shows a friendly message,
// and auto-reports to the server monitoring system.

import { apiFetch } from '../utils/api'
import { Component, ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  reported: boolean
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reported: false }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    if (this.state.reported) return
    this.setState({ reported: true })

    // Sentry — captures stack, breadcrumbs, replay, user context.
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack ?? undefined },
      },
    })

    // Auto-report to server
    apiFetch('/api/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack,
        component: info.componentStack?.split('\n')[1]?.trim() || 'unknown',
        url: window.location.pathname,
      }),
    }).catch(() => { /* silently fail if server is down */ })

    // Always log — helps diagnose silent crashes in production too
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-[300px] flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-xl">!</span>
            </div>
            <h3 className="text-[16px] font-[800] text-[#1C1A17] mb-2">
              {localStorage.getItem('bluaiq-lang') === 'en' ? 'Something went wrong' : 'Algo salió mal'}
            </h3>
            <p className="text-[12px] font-[500] text-[#9E9890] mb-4">
              {localStorage.getItem('bluaiq-lang') === 'en'
                ? 'This error has been automatically reported. Try refreshing the page.'
                : 'Este error se reportó automáticamente. Intenta recargar la página.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: undefined, reported: false })
                window.location.reload()
              }}
              className="bg-[#06539B] text-white text-[12px] font-[800] px-6 py-2.5 rounded-full hover:bg-[#14457E] transition-colors"
            >
              {localStorage.getItem('bluaiq-lang') === 'en' ? 'Refresh' : 'Recargar'}
            </button>
            {(import.meta as any).env?.DEV && this.state.error && (
              <pre className="mt-4 text-left text-[10px] text-red-500 bg-red-50 p-3 rounded-lg overflow-auto max-h-[200px]">
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack}
              </pre>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
